import { qa } from '@/qa/recorder';
import { Endpointer, type EndpointerOptions } from '../dsp/endpointer';
import { Resampler } from '../dsp/resample';
import { SCORER_SAMPLE_RATE } from '../types';
import captureWorkletUrl from './capture.worklet.ts?worker&url';

/**
 * Continuous listening: microphone (or simulated microphone) stream -> AudioWorklet -> 16 kHz
 * resampler -> endpointer -> one event per spoken utterance. Runs on the main thread; the work is
 * light (about 1% of a core) and inference happens in the scorer's worker.
 */

export interface Utterance {
  /** 16 kHz mono samples. */
  audio: Float32Array;
  durationMs: number;
  /** Longer than one word: cut at the maximum length. */
  cut: boolean;
  /** Loudest frame and the noise floor in dBFS, for the too-quiet check. */
  peakDb: number;
  noiseDb: number;
  /** Share of clipped samples (shouting). */
  clippedShare: number;
  /** Started while the app was talking (barge-in). */
  bargeIn: boolean;
}

export interface ListenerEvents {
  /**
   * The child started speaking (also while the app talks: the app should stop talking).
   * `bargeIn`: it began while the app talked. `childFirst`: the app is talking, but the voice
   * began before the app's current line did (echo cannot), so it is the child answering.
   */
  onSpeechStart?: (bargeIn: boolean, childFirst: boolean) => void;
  onUtterance?: (utterance: Utterance) => void;
  /** A sound too short to be a word (cough, click, tap). */
  onDiscarded?: () => void;
  /** Input level in [0, 1] about every 20 ms, for meters. */
  onLevel?: (level: number) => void;
}

/** Normal listening. */
const LISTENING: Partial<EndpointerOptions> = { onsetDb: 12, onsetFrames: 3, echoGuard: false };
/**
 * While the app talks, its own voice can reach the microphone: text-to-speech output may bypass the
 * browser's echo canceller. So barge-in needs clearly louder speech for 160 ms before it counts...
 */
const WHILE_PROMPTING: Partial<EndpointerOptions> = {
  onsetDb: 20,
  onsetFrames: 8,
  echoGuard: true,
};
/**
 * ...and an attempt that started while the app talked is kept only if the child went on speaking
 * for this long after the app fell silent (longer than a room's echo tail, about 150 ms);
 * otherwise it may be the app's own voice.
 */
const BARGE_IN_MIN_AFTER_MS = 200;

export class Listener {
  private readonly context: AudioContext;
  private readonly source: MediaStreamAudioSourceNode;
  private readonly node: AudioWorkletNode;
  private readonly sink: GainNode;
  private readonly resampler: Resampler;
  private readonly endpointer = new Endpointer();
  private readonly events: ListenerEvents;
  private prompting = false;
  private speechStartedWhilePrompting = false;
  /** When the app's current line began (endpointer clock). */
  private promptingSinceMs = 0;
  private muted = false;

  private constructor(
    context: AudioContext,
    stream: MediaStream,
    node: AudioWorkletNode,
    events: ListenerEvents,
  ) {
    this.context = context;
    this.events = events;
    this.node = node;
    this.source = context.createMediaStreamSource(stream);
    this.sink = context.createGain();
    this.sink.gain.value = 0;
    this.source.connect(node).connect(this.sink).connect(context.destination);
    this.resampler = new Resampler(context.sampleRate, SCORER_SAMPLE_RATE);
    node.port.onmessage = (event: MessageEvent<Float32Array>) => {
      this.process(event.data);
    };
  }

  /** The capture worklet module, loaded once per AudioContext. */
  private static readonly modules = new WeakMap<AudioContext, Promise<void>>();

  /**
   * Loads the capture worklet module into `context`. Call before opening the microphone: on iOS
   * Safari, adding an AudioWorklet module after getUserMedia distorts all playback on the context
   * (WebKit bugs 251091, 275801), which made the recorded voice crackle on iPhone.
   */
  static prepare(context: AudioContext): Promise<void> {
    let module = Listener.modules.get(context);
    if (!module) {
      module = context.audioWorklet.addModule(captureWorkletUrl);
      Listener.modules.set(context, module);
    }
    return module;
  }

  /** Starts listening to `stream`; call from a user gesture so audio can start. */
  static async start(
    stream: MediaStream,
    events: ListenerEvents,
    context: AudioContext,
  ): Promise<Listener> {
    await Listener.prepare(context);
    if (context.state === 'suspended') await context.resume();
    const node = new AudioWorkletNode(context, 'foe-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
    });
    return new Listener(context, stream, node, events);
  }

  get audioContext(): AudioContext {
    return this.context;
  }

  /**
   * The app is talking: require clearer speech before treating it as the child barging in.
   * `markSpeech` false: the app finished a line it kept speaking over the utterance (a resumed
   * line), so what follows its end says nothing about echo; the session judges by level instead.
   */
  setPrompting(prompting: boolean, markSpeech = true): void {
    // The app stops talking in the middle of an utterance (barge-in): measure what follows.
    if (this.prompting && !prompting && markSpeech && this.endpointer.speaking)
      this.endpointer.mark();
    if (prompting && !this.prompting) this.promptingSinceMs = this.endpointer.nowMs;
    this.prompting = prompting;
    this.endpointer.configure(prompting ? WHILE_PROMPTING : LISTENING);
  }

  /** A voice is being heard now (the start of a word included). */
  get hearingVoice(): boolean {
    return this.endpointer.hearingVoice;
  }

  /** The steady background level (dBFS): the endpointer's noise floor. */
  get noiseFloorDb(): number {
    return this.endpointer.noiseFloorDb;
  }

  /** Pause allowed inside one utterance (longer for prolonged sounds such as "rrrr-ed"). */
  setHangover(ms: number): void {
    this.endpointer.configure({ hangoverMs: ms });
  }

  /** Ignores input entirely, e.g. while the child's own recording plays back. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) this.endpointer.reset();
  }

  /** Ends any utterance in progress immediately (e.g. the mic button was pressed mid-word). */
  flush(): void {
    this.dispatch(this.endpointer.flush());
  }

  /** Stops listening; the AudioContext belongs to the caller and stays open. */
  close(): Promise<void> {
    this.node.port.onmessage = null;
    // Lets the processor return false so the audio thread drops it.
    this.node.port.postMessage('stop');
    this.source.disconnect();
    this.node.disconnect();
    this.sink.disconnect();
    return Promise.resolve();
  }

  private process(chunk: Float32Array) {
    const samples = this.resampler.push(chunk);
    // QA records what the microphone heard even while muted (the app's own echo is the point).
    qa.audio(samples);
    if (this.muted) return;
    this.dispatch(this.endpointer.push(samples));
    qa.level(this.endpointer.levelDb + this.endpointer.noiseFloorDb, this.endpointer.noiseFloorDb);
    this.events.onLevel?.(Math.min(1, Math.max(0, this.endpointer.levelDb / 40)));
  }

  private dispatch(events: ReturnType<Endpointer['push']>) {
    for (const event of events) {
      if (event.type === 'speech-start') {
        const childFirst = this.prompting && event.voiceAtMs < this.promptingSinceMs;
        qa.event('speech-start', { prompting: this.prompting, childFirst });
        this.speechStartedWhilePrompting = this.prompting && !childFirst;
        this.events.onSpeechStart?.(this.speechStartedWhilePrompting, childFirst);
      } else if (event.type === 'discarded') {
        qa.event('discarded', { reason: event.reason });
        // A blip while the app talks is most likely its own voice: learn its level, so the same
        // echo does not start a barge-in again (and is not judged over a resumed line).
        if (this.speechStartedWhilePrompting) this.endpointer.learnEchoLevel(event.peakDb);
        this.events.onDiscarded?.();
      } else if (
        this.speechStartedWhilePrompting &&
        (event.voicedMsSinceMark < BARGE_IN_MIN_AFTER_MS || (event.cut && this.prompting))
      ) {
        // Nothing followed once the app stopped, or it never stopped (cut while the app still
        // talked): most likely the app's own voice.
        qa.event('discarded', {
          reason: event.cut && this.prompting ? 'cut-while-talking' : 'echo',
          voicedMsSinceMark: event.voicedMsSinceMark,
          peakDb: round1(event.peakDb),
          durationMs: event.endMs - event.startMs,
        });
        this.endpointer.learnEchoLevel(event.peakDb);
        this.events.onDiscarded?.();
      } else {
        qa.event('utterance', {
          durationMs: event.endMs - event.startMs,
          cut: event.cut,
          peakDb: round1(event.peakDb),
          noiseDb: round1(event.noiseDb),
          clippedShare: event.clippedShare,
          bargeIn: this.speechStartedWhilePrompting,
          voicedMsSinceMark: event.voicedMsSinceMark,
        });
        this.events.onUtterance?.({
          audio: event.audio,
          durationMs: event.endMs - event.startMs,
          cut: event.cut,
          peakDb: event.peakDb,
          noiseDb: event.noiseDb,
          clippedShare: event.clippedShare,
          bargeIn: this.speechStartedWhilePrompting,
        });
      }
    }
  }
}

const round1 = (v: number) => Math.round(v * 10) / 10;
